import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, MapPin, Sparkles, Heart, Calendar } from "lucide-react";

const DIFFERENTIATORS = [
  { num: "01", title: "We're not a listing site.", body: "Every property is personally visited, vetted and photographed before it's live. If it doesn't meet our bar, it isn't on Appy Stays." },
  { num: "02", title: "We run our best properties end-to-end.", body: "For our managed partners, Appy Stays is the operator — brand, bookings, staff, vendors, pricing, ops. Hotel-grade experience, farmhouse pricing." },
  { num: "03", title: "We host our own community.", body: "Founder meetups, yoga Sundays, chef's tables. The Appy brand isn't just a booking engine; it's a community that celebrates together." },
];

const FOUNDERS = [
  { name: "Pratyusha", role: "Co-founder, Brand & Experience",
    bio: "Ex-hospitality operator. Picks every property, every photographer, every menu detail.",
    img: "https://api.dicebear.com/9.x/lorelei/svg?seed=Pratyusha&backgroundColor=E8F2EC" },
  { name: "Ajay", role: "Co-founder, Operations & Tech",
    bio: "Engineer-turned-operator. Built the host desk, the playbook, and the dashboards owners obsess over.",
    img: "https://api.dicebear.com/9.x/lorelei/svg?seed=Ajay&backgroundColor=E8F2EC" },
];

const TEAM = [
  { name: "Meera Kapoor", role: "Head of Host Desk", bio: "Leads the 12-person concierge team across all managed properties.",
    img: "https://api.dicebear.com/9.x/lorelei/svg?seed=Meera&backgroundColor=F4A930" },
  { name: "Rohan Desai", role: "Design Lead", bio: "Rebrands every managed property — identity, interiors, photography.",
    img: "https://api.dicebear.com/9.x/lorelei/svg?seed=Rohan&backgroundColor=7CB342" },
  { name: "Aanya Pillai", role: "Head of Partnerships", bio: "Picks the 1-in-20 properties that make it into Appy Managed.",
    img: "https://api.dicebear.com/9.x/lorelei/svg?seed=Aanya&backgroundColor=E8F2EC" },
  { name: "Zain Qureshi", role: "Revenue & Pricing", bio: "Prices every night. Averages a 2.4× uplift in 12 months.",
    img: "https://api.dicebear.com/9.x/lorelei/svg?seed=Zain&backgroundColor=F4A930" },
];

const TIMELINE = [
  { year: "2023", title: "First farmhouse", body: "We launched Olive Estate as our flagship managed property — 4 acres, 6 bedrooms." },
  { year: "2024", title: "Appy Managed launches", body: "We formalised the operator model. 5 properties live by year end. ₹2.1Cr in bookings." },
  { year: "2025", title: "Crossed ₹8.4Cr", body: "12 managed properties. 30+ in the marketplace. Appy Events hosts its first Founders Saturday." },
  { year: "2026", title: "Beyond Hyderabad", body: "Bengaluru and Goa rollouts begin. Aiming for 25+ managed properties by year end." },
];

const KPI = [
  { n: "30+", l: "Curated properties", icon: MapPin },
  { n: "12+", l: "Managed end-to-end", icon: Sparkles },
  { n: "1,000+", l: "Celebrations hosted", icon: Heart },
  { n: "4.92★", l: "Avg. guest rating", icon: Calendar },
];

export default function About() {
  return (
    <main data-testid="about-page" className="bg-[#FAFAF8]">
      {/* HERO */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-12 md:pt-16">
          <div className="relative grid lg:grid-cols-12 gap-0 rounded-[28px] overflow-hidden bg-[#0E2E24] text-[#FAFAF8] min-h-[480px]">
            <div className="lg:col-span-7 p-10 md:p-14 lg:p-16 flex flex-col justify-center order-2 lg:order-1">
              <div className="kicker text-[#F4A930] mb-5">About Appy Stays</div>
              <h1 className="font-sora text-4xl md:text-5xl lg:text-[3.6rem] font-semibold tracking-tight leading-[1.04]">
                We think people <span className="text-[#7CB342]">celebrate too quietly.</span>
              </h1>
              <p className="mt-6 max-w-xl text-[#FAFAF8]/85 text-lg leading-relaxed">
                Appy Stays began with a single farmhouse and a stubborn question — why does booking a beautiful place feel like a logistics job?
              </p>
              <p className="mt-3 max-w-xl text-[#FAFAF8]/70 leading-relaxed">
                So we rebuilt it. Quietly, around the guest and the owner.
              </p>
            </div>
            <div className="lg:col-span-5 relative aspect-[4/3] lg:aspect-auto order-1 lg:order-2">
              <img src="https://images.pexels.com/photos/28586227/pexels-photo-28586227.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1400&w=900" alt="" className="img-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* KPIs */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 mt-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {KPI.map((k) => (
            <div key={k.l} className="bg-white border border-[#0E2E24]/10 rounded-2xl p-6">
              <div className="h-10 w-10 rounded-xl bg-[#E8F2EC] flex items-center justify-center">
                <k.icon size={18} className="text-[#1F5A43]" />
              </div>
              <div className="mt-4 font-sora font-semibold text-3xl text-[#0E2E24]">{k.n}</div>
              <div className="mt-1 text-xs tracking-[0.18em] uppercase font-sora font-semibold text-[#1F5A43]">{k.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* WHAT WE ARE */}
      <Section kicker="01 · What is Appy Stays" heading="A curated platform for private stays, managed properties and community events.">
        We operate on three honest models — <strong>Marketplace</strong> (a vetted catalogue you can book in clicks),
        <strong> Managed</strong> (a selection of premium properties we run end-to-end), and <strong>Events</strong>
        (invite-only gatherings hosted at our properties). Everything we do is designed to make celebrations easier, better,
        more memorable — for the guest and for the owner.
      </Section>

      {/* DIFFERENTIATORS */}
      <section className="bg-white border-y border-[#0E2E24]/10 py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="kicker mb-4">02 · What makes us different</div>
          <h2 className="font-sora text-3xl md:text-5xl font-semibold tracking-tight text-[#0E2E24] max-w-3xl leading-tight">
            We don't list. We don't flip. We <span className="text-[#1F5A43]">operate.</span>
          </h2>
          <div className="mt-14 grid md:grid-cols-3 gap-5">
            {DIFFERENTIATORS.map((d) => (
              <div key={d.num} className="bg-[#FAFAF8] border border-[#0E2E24]/10 rounded-2xl p-8 hover-lift relative overflow-hidden">
                <span className="font-sora font-semibold text-[5rem] text-[#F4A930]/15 leading-none absolute -right-2 -top-4 select-none">{d.num}</span>
                <div className="kicker relative z-10">{d.num}</div>
                <h3 className="mt-4 font-sora text-xl font-semibold text-[#0E2E24] relative z-10">{d.title}</h3>
                <p className="mt-3 text-[#5B6B63] leading-relaxed relative z-10">{d.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 md:py-28">
        <div className="kicker mb-4">03 · Our journey</div>
        <h2 className="font-sora text-3xl md:text-4xl font-semibold tracking-tight text-[#0E2E24] max-w-2xl leading-tight">
          From one farmhouse to a city-spanning portfolio.
        </h2>
        <div className="mt-12 grid md:grid-cols-4 gap-5 relative">
          <div className="hidden md:block absolute left-0 right-0 top-7 h-px bg-[#0E2E24]/10" />
          {TIMELINE.map((t, i) => (
            <div key={t.year} className="relative" data-testid={`timeline-${t.year}`}>
              <div className="hidden md:flex h-14 w-14 rounded-full bg-[#0E2E24] text-[#F4A930] items-center justify-center font-sora font-semibold border-4 border-[#FAFAF8] relative z-10">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="mt-4 bg-white border border-[#0E2E24]/10 rounded-2xl p-6 hover-lift">
                <div className="kicker text-[#F4A930]">{t.year}</div>
                <h3 className="mt-2 font-sora text-base font-semibold text-[#0E2E24]">{t.title}</h3>
                <p className="mt-2 text-sm text-[#5B6B63] leading-relaxed">{t.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FOUNDERS — 2 only */}
      <section className="bg-[#0E2E24] text-[#FAFAF8] py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="kicker text-[#F4A930] mb-4">04 · Founders</div>
          <h2 className="font-sora text-3xl md:text-5xl font-semibold tracking-tight leading-tight">
            The two people running point.
          </h2>

          <div className="mt-14 grid md:grid-cols-2 gap-6 lg:gap-10">
            {FOUNDERS.map((f) => (
              <div key={f.name} className="bg-[#163b2f] border border-[#FAFAF8]/8 rounded-2xl p-8 md:p-10 flex items-start gap-6 hover:border-[#F4A930]/40 transition-colors" data-testid={`founder-${f.name.toLowerCase()}`}>
                <div className="relative h-28 w-28 md:h-32 md:w-32 rounded-2xl overflow-hidden bg-[#E8F2EC] shrink-0 border border-[#FAFAF8]/10">
                  <img src={f.img} alt={f.name} className="img-cover" />
                </div>
                <div>
                  <h3 className="font-sora text-2xl font-semibold">{f.name}</h3>
                  <div className="mt-1 text-sm text-[#F4A930] font-sora font-medium">{f.role}</div>
                  <p className="mt-3 text-[#FAFAF8]/80 leading-relaxed">{f.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 md:py-28">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <div className="kicker mb-3">05 · The team</div>
            <h2 className="font-sora text-3xl md:text-4xl font-semibold tracking-tight text-[#0E2E24] max-w-2xl leading-tight">
              Small, senior and entirely in-house.
            </h2>
          </div>
          <p className="max-w-sm text-[#5B6B63] font-sora">
            We stayed deliberately small. Every function has a senior owner. No outsourced ops, no vendor chaos.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {TEAM.map((t) => (
            <div key={t.name} className="bg-white border border-[#0E2E24]/10 rounded-2xl p-6 hover-lift" data-testid={`team-${t.name.toLowerCase().replace(/\s/g, '-')}`}>
              <div className="h-20 w-20 rounded-2xl overflow-hidden bg-[#E8F2EC]">
                <img src={t.img} alt={t.name} className="img-cover" />
              </div>
              <h3 className="mt-5 font-sora text-base font-semibold text-[#0E2E24]">{t.name}</h3>
              <div className="mt-1 text-xs text-[#1F5A43] font-sora font-semibold tracking-wide">{t.role}</div>
              <p className="mt-2 text-sm text-[#5B6B63] leading-relaxed">{t.bio}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="bg-[#E8F2EC] py-24 md:py-28">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <div className="kicker mb-5">Get in touch</div>
          <h2 className="font-sora text-3xl md:text-5xl font-semibold tracking-tight text-[#0E2E24] leading-[1.05]">
            Let's plan something <span className="text-[#1F5A43]">beautiful.</span>
          </h2>
          <p className="mt-5 max-w-xl mx-auto text-[#5B6B63]">
            Whether you're booking a celebration or listing a property — we'd love to hear from you.
          </p>
          <div className="mt-10 flex justify-center gap-3 flex-wrap">
            <Link to="/stays">
              <Button className="bg-[#0E2E24] hover:bg-[#1F5A43] text-[#FAFAF8] rounded-full px-7 py-6 font-sora font-medium gap-2" data-testid="about-explore">
                Explore stays <ArrowRight size={16} />
              </Button>
            </Link>
            <Link to="/partner">
              <Button variant="outline" className="rounded-full px-7 py-6 border-[#0E2E24]/20 text-[#0E2E24] bg-white hover:bg-[#FAFAF8] font-sora font-medium" data-testid="about-partner">
                List your property
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}

function Section({ kicker, heading, children }) {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 md:py-28">
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <div className="kicker">{kicker}</div>
        </div>
        <div className="lg:col-span-8">
          <h2 className="font-sora text-3xl md:text-4xl font-semibold tracking-tight text-[#0E2E24] leading-tight">
            {heading}
          </h2>
          <p className="mt-6 text-[#5B6B63] text-lg leading-relaxed max-w-2xl">{children}</p>
        </div>
      </div>
    </section>
  );
}
